import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminPromotionAPI } from '../services/api';
import { useAlert } from '../context/AlertContext';
import PromotionModal from '../components/PromotionModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import '../styles/Promotions.css';

const Promotions = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [promotionToDelete, setPromotionToDelete] = useState(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchPromotions = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 10,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(statusFilter && { status: statusFilter })
      };

      const response = await adminPromotionAPI.getAll(params);
      setPromotions(response.data.data || []);
      setTotalPages(response.data.pagination.totalPages || 1);
      setError(null);
    } catch (err) {
      console.error('Error fetching promotions:', err);
      setError(err.response?.data?.message || 'Failed to fetch promotions');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handleCreate = () => {
    setSelectedPromotion(null);
    setShowModal(true);
  };

  const handleEdit = (promotion) => {
    setSelectedPromotion(promotion);
    setShowModal(true);
  };

  const handleDeleteClick = (promotion) => {
    setPromotionToDelete(promotion);
    setShowDeleteModal(true);
  };

  const handlePromotionDeleted = () => {
    setShowDeleteModal(false);
    setPromotionToDelete(null);
    fetchPromotions();
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      if (currentStatus === 'Active') {
        await adminPromotionAPI.deactivate(id);
      } else {
        await adminPromotionAPI.activate(id);
      }
      fetchPromotions();
    } catch (err) {
      console.error('Error updating status:', err);
      showAlert(err.response?.data?.error || "Failed to update promotion status", 'error');
    }
  };

  if (loading && promotions.length === 0) {
    return (
      <div className="promotions-container">
        <div className="loading">Loading promotions...</div>
      </div>
    );
  }

  return (
    <div className="promotions-container">
      <div className="promotions-header">
        <h1>Promotion Management</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          + Add New Promotion
        </button>
      </div>

      <div className="promotions-toolbar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search promotions..."
            value={search}
            onChange={handleSearch}
          />
        </div>

        <div className="filters">
          <select value={statusFilter} onChange={handleStatusFilterChange}>
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Expired">Expired</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {promotions.length === 0 ? (
        <div className="empty-state">
          <p>No promotions found</p>
          <button className="btn btn-primary" onClick={handleCreate}>
            Create Your First Promotion
          </button>
        </div>
      ) : (
        <div className="promotions-table-wrapper">
          <table className="promotions-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Status</th>
                <th>Validity</th>
                <th>Categories</th>
                <th>Products</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {promotions.map(promotion => (
                <tr key={promotion._id}>
                  <td>
                    <div className="promotion-image">
                      {promotion.bannerImage ? (
                        <img
                          src={promotion.bannerImage}
                          alt={promotion.title}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="no-image">No Image</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="promotion-title-cell">
                      <h3>{promotion.title}</h3>
                      <p className="promotion-description-short">{promotion.description}</p>
                    </div>
                  </td>
                  <td>
                    <span className={`btn`}>
                      {promotion.status}
                    </span>
                  </td>
                  <td>
                    <div className="validity-cell">
                      <div>{new Date(promotion.startDate).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}</div>
                      <div className="validity-to">to {new Date(promotion.endDate).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}</div>
                    </div>
                  </td>
                  <td>
                    <div className="count-cell">
                      {promotion.applicableCategories?.length || 0} categories
                    </div>
                  </td>
                  <td>
                    <div className="count-cell">
                      {promotion.featuredProducts?.length || 0} products
                    </div>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="action-btn btn-view-icon"
                        onClick={() => navigate(`/promotions/${promotion._id}`)}
                        title="View"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      </button>
                      <button
                        className="action-btn btn-edit-icon"
                        onClick={() => handleEdit(promotion)}
                        title="Edit"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button
                        className="action-btn btn-toggle-icon"
                        onClick={() => handleStatusToggle(promotion._id, promotion.status)}
                        title={promotion.status === 'Active' ? 'Deactivate' : 'Activate'}
                      >
                        <svg
                          width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="2"
                          stroke={
                            promotion.status === 'Active' ? '#22c55e' :
                            promotion.status === 'Scheduled' ? '#3b82f6' :
                            promotion.status === 'Expired' ? '#9ca3af' :
                            '#ef4444'
                          }
                        >
                          <rect x="1" y="5" width="22" height="14" rx="7" ry="7"></rect>
                          <circle
                            cx={promotion.status === 'Active' ? '16' : '8'}
                            cy="12" r="3"
                            fill={
                              promotion.status === 'Active' ? '#22c55e' :
                              promotion.status === 'Scheduled' ? '#3b82f6' :
                              promotion.status === 'Expired' ? '#9ca3af' :
                              '#ef4444'
                            }
                          ></circle>
                        </svg>
                      </button>
                      <button
                        className="action-btn btn-delete-icon"
                        onClick={() => handleDeleteClick(promotion)}
                        title="Delete"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}

      <PromotionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={fetchPromotions}
        promotion={selectedPromotion}
      />

      {showDeleteModal && (
        <DeleteConfirmModal
          item={promotionToDelete}
          itemType="promotion"
          onClose={() => {
            setShowDeleteModal(false);
            setPromotionToDelete(null);
          }}
          onDeleted={handlePromotionDeleted}
          deleteFunction={adminPromotionAPI.delete}
        />
      )}
    </div>
  );
};

export default Promotions;
